/* -------------------------------------------------------------------------- */
/*                               Angular Imports                              */
/* -------------------------------------------------------------------------- */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PushNotificationService } from './services/push-notification.service';
// import { SnowplowService } from './services/snowplow.service';

/* -------------------------------------------------------------------------- */
/*                               Environment/Utils                            */
/* -------------------------------------------------------------------------- */
import { environment } from 'environments/environment';
import * as AOS from 'aos';
import Swal from 'sweetalert2';
import { lastValueFrom } from 'rxjs';

/* -------------------------------------------------------------------------- */
/*                            Capacitor & Plugins                             */
/* -------------------------------------------------------------------------- */
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';

/* -------------------------------------------------------------------------- */
/*                            Application Services                            */
/* -------------------------------------------------------------------------- */
import { AuthService } from './core/auth/auth.service';
import { ShopifyService } from './core/shopify/shopify.service';
import { DeviceService } from './services/device.service';
import { SocialService } from './services/social-service';
import { FuseConfigService } from '@fuse/services/config';
import { GoogleAuthService } from 'app/services/google-auth.service';
import { SeoService } from 'app/core/seo/seo.service';

/* -------------------------------------------------------------------------- */
/*                             Shared / Components                            */
/* -------------------------------------------------------------------------- */
import { SharedMethods } from './shared/shared-methods';
import { RotatePromptComponent } from './modules/member/reflections/rotate/rotate-phone.component';
// import { SmartBannerComponent } from './shared/smart-banner/smart-banner.component';

/* -------------------------------------------------------------------------- */
/*                                Root Component                              */
/* -------------------------------------------------------------------------- */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [
    RouterOutlet,
    SharedMethods,
    ServiceWorkerModule,
    RotatePromptComponent,
    CommonModule
  ],
  host: { 'window:beforeunload': 'doSomething' }
})
export class AppComponent {
  /* ------------------------------------------------------------------------ */
  /*                               State Variables                            */
  /* ------------------------------------------------------------------------ */
  public isMobile: boolean;
  public isTablet: boolean;
  private isNative = Capacitor.isNativePlatform();

  /* ------------------------------------------------------------------------ */
  /*                               Constructor                                */
  /* ------------------------------------------------------------------------ */
  constructor(
    private _authService: AuthService,
    private router: Router,
    private _shopifyService: ShopifyService,
    public sharedMethods: SharedMethods,
    private deviceService: DeviceService,
    private _pushService: PushNotificationService,
    private socialService: SocialService,
    private _fuseConfigService: FuseConfigService,
    private googleAuth: GoogleAuthService,
    private seoService: SeoService
  ) {
    this.googleAuth.initialize();
    this.seoService.init();
    // Reset Shopify flag when navigating away from /home
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects !== '/home/' && event.urlAfterRedirects !== '/home') {
          this._shopifyService.shopifyLoaded = false;
        }
      }
    });
  }

  /* ------------------------------------------------------------------------ */
  /*                           Lifecycle Hooks                                */
  /* ------------------------------------------------------------------------ */

  ngAfterViewInit() {
    // Remove SweetAlert backdrop on load
    const swalContainer = document.querySelector('.swal2-container');
    if (swalContainer) {
      swalContainer.classList.remove('swal2-backdrop-show');
    }
  }

  async ngOnInit() {

    // `POST /v1/auth/forgot-password` (`AuthService.forgotPassword()`) never passes a
    // `redirect_to` — GoTrue's mailed recovery link therefore lands on the Supabase
    // project's own configured Site URL (the app's bare origin), NOT necessarily on
    // `/reset-password`, with `?token_hash=...&type=recovery` in the query string
    // regardless of which path it lands on. Every route in this SPA (including the
    // unconditional `'' -> 'home'` redirect in `app.routes.ts`) otherwise drops those
    // query params on the floor. Catch that exact shape here, once, at bootstrap, and
    // forward to the page that actually knows how to consume `token_hash`
    // (`AuthResetPasswordComponent`) — this makes the mailed link reachable regardless
    // of what the Supabase project's own Site URL happens to be set to.
    if (window.location.pathname !== '/reset-password') {
      const recoveryParams = new URLSearchParams(window.location.search);
      const recoveryTokenHash = recoveryParams.get('token_hash');
      if (recoveryTokenHash && recoveryParams.get('type') === 'recovery') {
        this.router.navigate(['/reset-password'], { queryParams: { token_hash: recoveryTokenHash } });
      }
    }

    // Device observers
    this.deviceService.isMobile$.subscribe(value => this.isMobile = value);
    this.deviceService.isIpadPortrait$.subscribe(value => this.isTablet = value);

    // Init AOS animation
    AOS.init();

    // Set user agent if not already stored
    if (!localStorage.getItem('_USERAGENT')) {
      localStorage.setItem('_USERAGENT', navigator.userAgent + '+' + new Date().toUTCString());
    }

    // Check if route requires the service worker
    const windowLocation = window.location.pathname;
    if (windowLocation.includes('member')) {
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
        registrationStrategy: 'registerWhenStable:30000'
      });
    }

    // Initialize push notifications if user is authenticated
    if (this._authService.accessToken) {
      try {
        await this._pushService.initialize();
      } catch (error) {
        console.error('Failed to initialize push notifications:', error);
      }
    }

    // Redirect to login if native app without token
    if (!this._authService.accessToken && this.isNative) {
      this.router.navigate(['/sign-in']);
    }

  }

  /* ------------------------------------------------------------------------ */
  /*                           Private Methods                                */
  /* ------------------------------------------------------------------------ */

  // `deviceChecker()` (fix-pass, DEMO-RML-WEB-MEMBER-MENU-AUDIT, coordinator follow-up)
  // removed entirely, not rewired, after reading its own success branch. It used to
  // call `ProfileManagementService.listDevices()` against a retired legacy device
  // grid RPC on EVERY app load, for every authenticated member; an empty or
  // differently-shaped response signed the member out (`localStorage.removeItem
  // ('accessToken')`). Today the call errors and the error branch is deliberately
  // silent, so nothing happens; that is an accident of the endpoint being dead, not a
  // safety property. Rewiring this to the real `GET /v1/devices` (`DevicesService`,
  // already correctly wired elsewhere in this app for "Manage Devices") would NOT be
  // safe: nothing anywhere in this front-end ever calls the matching device
  // REGISTRATION route, so a genuine member's device list is always empty today,
  // which is exactly the condition that trips the sign-out branch above. Rewiring it
  // would sign every member out on every single page load. The new API's own
  // device-cap enforcement is also off by default
  // (`DEVICE_CAP_ENFORCEMENT_ENABLED`, `rml-platform/apps/api/src/routes/devices.ts`),
  // so there is no live policy this check would even be enforcing. Behaviour lost: the
  // (never-functioning-here) ability to force a local sign-out when a member's
  // account has zero devices recorded server-side; nothing replaces it, because
  // nothing on the new API produces that signal meaningfully today either.
}
